// PackagePanel — apply/view/remove a PM-JAY/CMCHIS/hospital package on one
// admission (Insurance Claims Phase 6.1-6.3: docs/HMS_V4_INSURANCE_CLAIMS_
// DESIGN.md §4.4). Used inside IPModule.tsx's AdmissionDetail, right beside
// CoveragePanel (same toggle-button convention: hidden until "+ Apply
// package" is clicked, shows the current active package — if any — with a
// "Remove package" action otherwise).
//
// Deliberately a thin picker, not a full form: a package's own fields
// (includes/excludes heads, rate, LOS days) are set once, in Admin ->
// Insurance Claims -> Payer Tariffs & Packages, not re-entered here. This
// screen only answers "which package, if any, applies to this stay" — the
// same split CoveragePanel draws between capturing a policy's particulars
// here and configuring payers/service-master in the admin tab.
import { useState } from 'react';
import { ApiError } from '@/lib/api/client';
import { Button } from '@/components/ui/Button';
import { NativeSelect } from '@/components/ui/Input';
import { FormField } from '@/components/ui/FormField';
import { Badge } from '@/components/ui/Badge';
import { Section } from '@/components/ui/Section';
import { LoadingState, ErrorState } from '@/components/ui/States';
import { getPackages } from '@/features/admin/api';
import type { Package } from '@/features/admin/types';
import { useAdmissionPackages, useApplyPackage, useRemovePackage } from './hooks';

export interface PackagePanelProps {
  admissionId: number;
  onChanged?: () => void;
}

export function PackagePanel({ admissionId, onChanged }: PackagePanelProps) {
  const packagesQuery = useAdmissionPackages(admissionId);
  const applyMutation = useApplyPackage(admissionId);
  const removeMutation = useRemovePackage(admissionId);

  const [catalog, setCatalog] = useState<Package[] | null>(null);
  const [selectedPackageId, setSelectedPackageId] = useState('');
  const [error, setError] = useState('');

  const active = (packagesQuery.data ?? []).find((p) => p.status === 'active') ?? null;

  const loadCatalog = () => {
    if (catalog) return;
    getPackages({ active: 'true' }).then(setCatalog).catch(() => setCatalog([]));
  };

  const apply = async () => {
    if (!selectedPackageId) return;
    setError('');
    try {
      await applyMutation.mutateAsync(Number(selectedPackageId));
      setSelectedPackageId('');
      onChanged?.();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : e instanceof Error ? e.message : String(e));
    }
  };

  const remove = async () => {
    if (!active) return;
    setError('');
    try {
      await removeMutation.mutateAsync(active.id);
      onChanged?.();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : e instanceof Error ? e.message : String(e));
    }
  };

  if (packagesQuery.isLoading) {
    return <Section title="Package"><LoadingState label="Loading package…" /></Section>;
  }
  if (packagesQuery.isError) {
    return (
      <Section title="Package">
        <ErrorState message={packagesQuery.error instanceof ApiError ? packagesQuery.error.message : 'Could not load the applied package'} onRetry={() => packagesQuery.refetch()} />
      </Section>
    );
  }

  return (
    <Section title="Package">
      {active ? (
        <div className="flex flex-col gap-2">
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant="info">📦 {active.name} ({active.code})</Badge>
            <span className="text-[11px] text-ink-muted">
              ₹{Number(active.rate_snapshot).toFixed(2)}{active.los_days_snapshot != null ? ` · ${active.los_days_snapshot} day(s)` : ''}
            </span>
          </div>
          {active.includes_heads.length > 0 && (
            <div className="text-[11px] text-ink-muted">Includes: {active.includes_heads.join(', ')}</div>
          )}
          {active.excludes_heads.length > 0 && (
            <div className="text-[11px] text-ink-muted">Excludes: {active.excludes_heads.join(', ')}</div>
          )}
          {error && <div className="text-[12px] text-danger-ink">{error}</div>}
          <div>
            <Button size="sm" variant="danger" loading={removeMutation.isPending} onClick={remove}>Remove package</Button>
          </div>
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          <div className="text-[11.5px] text-ink-muted">
            No package applied. Applying one zero-rates every new charge whose head is in that package&apos;s
            Includes list — set up in Admin → Insurance Claims → Payer Tariffs &amp; Packages.
          </div>
          <div className="flex flex-wrap items-end gap-2">
            <FormField label="Package" className="min-w-[260px]">
              <NativeSelect value={selectedPackageId} onFocus={loadCatalog} onChange={(e) => setSelectedPackageId(e.target.value)}>
                <option value="">{catalog === null ? 'Click to load packages…' : catalog.length === 0 ? 'No active packages' : 'Select a package…'}</option>
                {(catalog ?? []).map((p) => <option key={p.id} value={p.id}>{p.name} ({p.code}) · ₹{Number(p.rate).toFixed(2)}</option>)}
              </NativeSelect>
            </FormField>
            <Button size="sm" disabled={!selectedPackageId} loading={applyMutation.isPending} onClick={apply}>Apply package</Button>
          </div>
          {error && <div className="text-[12px] text-danger-ink">{error}</div>}
        </div>
      )}
    </Section>
  );
}
